import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { DeleteOutlined } from '@ant-design/icons';
import { Row, Col, Modal, Table, Button, Popconfirm } from 'antd';

import LspDelOrdIndexActions from '../../Stores/LspDelOrdIndex/Actions';

const LspDelOrdIndexExportDialog = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const workspaceIsVisible = useSelector(state => state.lspDelOrdIndex.workspaceIsVisible);
  const selectedDocuments = useSelector(state => state.lspDelOrdIndex.selectedDocuments);
  const workspaceIsLoading = useSelector(state => state.lspDelOrdIndex.workspaceIsLoading);
  const fetchIsLoading = useSelector(state => state.lspDelOrdIndex.fetchIsLoading);

  const setWorkspaceVisible = useCallback(
    visible => {
      dispatch(LspDelOrdIndexActions.lspDelOrdIndexSetWorkspaceVisible(visible));
    },
    [dispatch]
  );

  const removeSelectedDocuments = useCallback(
    docs => {
      dispatch(LspDelOrdIndexActions.lspDelOrdIndexRemoveSelectedDocuments(docs));
    },
    [dispatch]
  );

  const handleCancel = useCallback(() => {
    setWorkspaceVisible(false);
  }, [setWorkspaceVisible]);

  const getSelectedDocumentColumns = useCallback(() => {
    return [
      {
        align: 'left',
        title: intl.formatMessage({ id: 'salesman' }),
        dataIndex: 'salesman_username',
        sorter: (a, b) => `${a.salesman_username}`.localeCompare(b.salesman_username),
        key: 'salesman',
        render: (text, record) => <b>{record.salesman_username}</b>
      },
      {
        align: 'left',
        title: intl.formatMessage({ id: 'delivery_point' }),
        dataIndex: 'delivery_point_code',
        sorter: (a, b) => `${a.delivery_point_code}`.localeCompare(b.delivery_point_code),
        key: 'delivery_point',
        render: (text, record) => (
          <>
            <b>{record.delivery_point_code}</b>
            <br />
            {record.delivery_point_company_name_01}
          </>
        )
      },
      {
        align: 'left',
        title: intl.formatMessage({ id: 'doc_code' }),
        dataIndex: 'doc_code',
        sorter: (a, b) => `${a.doc_code}`.localeCompare(b.doc_code),
        key: 'doc_code',
        render: (text, record) => (
          <>
            <b>{record.doc_code}</b>
            <br />
            {record.str_doc_status}
          </>
        )
      },
      {
        align: 'left',
        title: intl.formatMessage({ id: 'doc_date' }),
        dataIndex: 'doc_date',
        sorter: (a, b) => new Date(a.doc_date).getTime() - new Date(b.doc_date).getTime(),
        key: 'doc_date',
        render: (text, record) => <>{moment(record.doc_date).format('YYYY-MM-DD')}</>
      },
      {
        align: 'right',
        title: intl.formatMessage({ id: 'net_amt' }),
        dataIndex: 'net_amt',
        sorter: (a, b) => a.net_amt - b.net_amt,
        key: 'net_amt',
        render: (text, record) => (
          <div style={{ textAlign: 'right' }}>
            {new Intl.NumberFormat([], {
              style: 'decimal',
              minimumFractionDigits: Number(process.env.REACT_APP_DECIMAL_SCALE),
              maximumFractionDigits: Number(process.env.REACT_APP_DECIMAL_SCALE)
            }).format(record.net_amt)}
          </div>
        )
      },
      {
        key: 'action',
        render: (text, record) => (
          <Popconfirm
            placement="left"
            title={intl.formatMessage({
              id: 'are_you_sure_to_remove_this_document'
            })}
            onConfirm={() => removeSelectedDocuments([record])}
            okText={intl.formatMessage({ id: 'yes' })}
            cancelText={intl.formatMessage({ id: 'cancel' })}
          >
            <Button type="dashed" icon={<DeleteOutlined />} />
          </Popconfirm>
        )
      }
    ];
  }, [intl, removeSelectedDocuments]);

  return (
    <Modal
      open={workspaceIsVisible}
      title={intl.formatMessage({ id: 'selected_documents' })}
      width="80%"
      centered
      onCancel={handleCancel}
      footer={[
        <Button
          loading={fetchIsLoading || workspaceIsLoading}
          key="confirm"
          type="primary"
          onClick={() => {
            const hdrIds = selectedDocuments.map(doc => doc.id);
            dispatch(LspDelOrdIndexActions.lspDelOrdExportPurchaseInvoices(hdrIds));
            
          }}
        >
          {intl.formatMessage({ id: 'export_purchase_invoice' })}
        </Button>
      ]}
    >
      <Row justify="center" gutter={[0, 8]}>
        <Col span={24}>
          <Table
            rowKey="id"
            pagination={false}
            columns={getSelectedDocumentColumns()}
            dataSource={selectedDocuments}
            loading={workspaceIsLoading || fetchIsLoading}
            bordered
            title={() => <b>{intl.formatMessage({ id: 'selected_documents' })}</b>}
          />
        </Col>
      </Row>
    </Modal>
  );
};

export default LspDelOrdIndexExportDialog;
